import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';

import { AuthService, setDsToken } from '@lib/auth';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { NgOtpInputComponent, NgOtpInputModule } from 'ng-otp-input';

import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';

import { PHONE_REGEX } from '@lib/core/constants/general.constant';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { TimerComponent } from '../timer/timer.component';

enum AuthSteps {
  PhoneNumber = 1,
  Otp = 2,
}

@Component({
  selector: 'auth-otp',
  standalone: true,
  imports: [ReactiveFormsModule, NgOtpInputModule, LoaderComponent, MatTooltip, TimerComponent, MatHint, MatFormField, MatInput, MatIcon, TranslatePipe, MatLabel, MatError],
  templateUrl: './auth-otp.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthOtpComponent implements OnInit {
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store);

  protected currentStep = signal<AuthSteps>(AuthSteps.PhoneNumber);
  protected readonly authSteps = AuthSteps;
  protected form = new FormGroup({
    phone: new FormControl('', [Validators.required, Validators.pattern(PHONE_REGEX)]),
    password: new FormControl('', Validators.required),
  });

  @ViewChild('otp') protected otp!: NgOtpInputComponent;

  protected readonly loading = signal<boolean>(false);
  protected readonly waitForResend = signal<boolean>(false);

  constructor() {
    this.iconRegistry.addSvgIcon('viber', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/ic_viber.svg'));
  }

  ngOnInit() {
    this.store.dispatch(setDsToken({ dsToken: null }));
  }

  protected async selectNextStep() {
    if (this.canSelectNextStep() && this.phone.value) {
      const sent = await this.authService.requestOtp(this.phone.value);

      switch (this.currentStep()) {
        case AuthSteps.PhoneNumber:
          this.loading.set(true);
          if (sent) {
            this.currentStep.set(AuthSteps.Otp);
            this.startResendTimer();
          } else {
            this.phone.setErrors({ invalid: true });
            this.authService.successAuthResult$.next(false);
          }
          this.loading.set(false);
          break;
        case AuthSteps.Otp:
          break;
      }
    }
  }

  protected canSelectNextStep(): boolean {
    switch (this.currentStep()) {
      case AuthSteps.PhoneNumber:
        return this.phone.valid;
      case AuthSteps.Otp:
        return this.password.valid;
    }
  }

  protected async requestOtpCode() {
    this.loading.set(true);
    await this.authService.requestOtp(this.phone.value!);
    this.loading.set(false);
    this.startResendTimer();
  }

  protected async requestViberCode() {
    this.loading.set(true);
    await this.authService.requestViber(this.phone.value!);
    this.loading.set(false);
    this.startResendTimer();
  }

  private startResendTimer() {
    this.waitForResend.set(true);
  }

  protected countdownCompleted() {
    this.waitForResend.set(false);
  }

  protected async requestCodeVerification(code: string) {
    const form = this.form.value;
    if (form.phone && code && code.length === 6) {
      this.loading.set(true);
      const valid = await this.authService.verifyCode(form.phone, code);
      if (valid) {
        this.authService.successAuthResult$.next(true);
      } else {
        this.password.setErrors({ invalid: true });
        this.password.markAsDirty();
        this.authService.successAuthResult$.next(false);
      }

      this.loading.set(false);
      this.password.setValue('');
      this.otp?.setValue(null);
    }
  }

  protected async goBack() {
    if (this.currentStep() === AuthSteps.Otp) {
      this.currentStep.set(AuthSteps.PhoneNumber);
    }
  }

  protected get phone() {
    return this.form.controls.phone as FormControl;
  }

  protected get password(): FormControl {
    return this.form.controls.password as FormControl;
  }
}
